import { useLocation } from 'react-router-dom';
import { useMachine } from '@/_zustand/machine';

/**
 * useCompanyFlow
 *
 * Hook centralizado para gestionar el flujo de empresa (/company).
 *
 * - isCompanyFlow: true si la ruta actual comienza con /company
 * - companyHomePath: la ruta home del flujo de empresa (ej: /company/5)
 *   Prioridad: 1) localStorage 'companyHome'  2) empresa_id de zustand  3) '/'
 * - getCompanyRoute(path): devuelve la ruta prefijada con /company si estamos en company flow
 *   Rutas de auth (login, register, etc.) quedan sin prefijo
 */
const AUTH_ROUTES = ['login', 'register', 'forgot-password', 'recovery-password'];

/**
 * Determina la ruta de redirección post-login.
 * Prioridad: 1) companyHome (flujo empresa)  2) lastPatch  3) '/'
 * No depende de hooks, se puede llamar dentro de callbacks async.
 */
export const getPostLoginPath = () => {
  const companyHome = localStorage.getItem('companyHome');
  if (companyHome) return companyHome;

  const lastPatch = localStorage.getItem('lastPatch');
  return lastPatch || '/';
};

export const useCompanyFlow = () => {
  const { pathname } = useLocation();
  const { empresa_id } = useMachine();

  // Está en company flow si la ruta comienza con /company
  // O si tiene companyHome guardado (p.ej. cuando está temporalmente en /login, /register, etc.)
  const isCompanyFlow =
    pathname.startsWith('/company') || !!localStorage.getItem('companyHome');

  // Determina el home de la empresa:
  // 1. Lo que quedó guardado en localStorage al entrar por /company/:id
  // 2. empresa_id del store de zustand
  // 3. Fallback a '/'
  const getCompanyHomePath = () => {
    const stored = localStorage.getItem('companyHome');
    if (stored) return stored;

    if (isCompanyFlow && empresa_id) return `/company/${empresa_id}`;

    return '/';
  };

  const companyHomePath = getCompanyHomePath();

  /**
   * getCompanyRoute(path)
   * Ejemplo:
   *   isCompanyFlow=true  + 'orders'   => '/company/orders'
   *   isCompanyFlow=true  + '/'        => companyHomePath (/company/5)
   *   isCompanyFlow=false + 'orders'   => '/orders'
   *   isCompanyFlow=true  + 'login'    => '/login' (rutas de auth siempre globales)
   */
  const getCompanyRoute = (path) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;

    // Home
    if (cleanPath === '' || path === '/') {
      return companyHomePath;
    }

    // Rutas de autenticación — siempre globales
    if (AUTH_ROUTES.includes(cleanPath)) {
      return `/${cleanPath}`;
    }

    return isCompanyFlow ? `/company/${cleanPath}` : `/${cleanPath}`;
  };

  return {
    isCompanyFlow,
    companyHomePath,
    getCompanyRoute,
  };
};
